import { useState } from "react";
import type {
  FindingActionKind,
  ValidationResult,
} from "../../types";
import {
  actionMap,
  activeFindings,
  failGateSatisfied,
  findingId,
  isMappable,
  noteMap,
  summaryText,
  unaddressedFailCount,
} from "../../lib/findings";
import FindingMarker from "./FindingMarker";
import FindingContextCard from "./FindingContextCard";

// Compact summary bar shown below the ValidationBadge. Replaces the old
// block list. Shows the counts line; expands to list findings that have no
// inline location (completeness / general), plus a "Needs attention" list
// of anything flagged-for-edit. When fail-level findings remain
// unaddressed, shows the review gate note.
export default function FindingsSummaryBar({
  validation,
  onAction,
  // When true the prose already renders mappable findings inline, so the
  // bar lists only unmappable ones. When false (markdown/structured
  // surfaces) it lists every active finding.
  inlineMappable = true,
}: {
  validation: ValidationResult;
  onAction: (
    findingId: string,
    kind: FindingActionKind,
    note?: string,
  ) => void;
  inlineMappable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const actions = actionMap(validation);
  const notes = noteMap(validation);
  const active = activeFindings(validation, actions);
  if (active.length === 0) return null;

  const summary = summaryText(validation, actions);
  const barFindings = validation.issues
    .map((f, i) => ({ f, id: findingId(f, i) }))
    .filter(({ f, id }) => actions[id] !== "dismissed")
    .filter(({ f }) => (inlineMappable ? !isMappable(f) : true));
  const flagged = validation.issues
    .map((f, i) => ({ f, id: findingId(f, i) }))
    .filter(({ id }) => actions[id] === "flagged");

  const failRemaining = unaddressedFailCount(validation, actions);
  const gateOk = failGateSatisfied(validation, actions);

  return (
    <div className="findings-bar">
      <button
        type="button"
        className="findings-bar-summary"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {summary || "All findings addressed"}
        <span className="findings-bar-caret">{open ? " ▴" : " ▾"}</span>
      </button>

      {!gateOk && (
        <div className="findings-bar-gate">
          ⚠ Review the {failRemaining} flagged issue
          {failRemaining === 1 ? "" : "s"} (dismiss or accept each) to enable
          export / copy.
        </div>
      )}

      {open && (
        <div className="findings-bar-detail">
          {barFindings.length > 0 && (
            <ul className="findings-bar-list">
              {barFindings.map(({ f, id }) => (
                <li key={id}>
                  <FindingMarker
                    finding={f}
                    action={actions[id]}
                    note={notes[id]}
                    onAction={(kind, note) => onAction(id, kind, note)}
                    inline={false}
                  />{" "}
                  {f.description}
                  <FindingContextCard finding={f} />
                </li>
              ))}
            </ul>
          )}
          {flagged.length > 0 && (
            <div className="findings-bar-attention">
              <div className="findings-bar-attention-head">
                Needs attention ({flagged.length})
              </div>
              <ul>
                {flagged.map(({ f, id }) => (
                  <li key={id}>
                    {f.description}
                    {notes[id] && (
                      <div className="findings-bar-note">
                        <span className="finding-flag-saved-label">
                          Your note:
                        </span>{" "}
                        {notes[id]}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
